import React, { useEffect, useState } from 'react';
import Chart from 'chart.js/auto';
import { cleanCopy, guid, makeArray } from '../../../utils';
import { get, set, first } from '../../../lib/lodash';
import { avgItems } from '../utils';
import useStyles from '../styles';
import clsx from 'clsx';
import { DropDown } from '../../dropdown';
import { ListItemIcon } from '@mui/material';

const COLORS = [
  '#D34216ff',
  '#0043D9ff',
  '#60d394ff',
  '#fdcb47ff',
  '#c76b98ff',
  '#b01400ff',
  '#4eb3ffff',
  '#20751cff',
  '#cc7b00ff',
  '#c12dc1ff',
  '#ee8d5cff',
  '#001682ff',
  '#3fbe5aff',
  '#fda102ff',
  '#dda6afff',

  '#D34216ff',
  '#0043D9ff',
  '#60d394ff',
  '#fdcb47ff',
  '#c76b98ff',
  '#b01400ff',
  '#4eb3ffff',
  '#20751cff',
  '#cc7b00ff',
  '#c12dc1ff',
  '#ee8d5cff',
  '#001682ff',
  '#3fbe5aff',
  '#fda102ff',
  '#dda6afff',

  '#D34216ff',
  '#0043D9ff',
  '#60d394ff',
  '#fdcb47ff',
  '#c76b98ff',
  '#b01400ff',
  '#4eb3ffff',
  '#20751cff',
  '#cc7b00ff',
  '#c12dc1ff',
  '#ee8d5cff',
  '#001682ff',
  '#3fbe5aff',
  '#fda102ff',
  '#dda6afff',
];

const makeDisplayNumber = (v = '') => {
  const val = parseFloat(`${v || ''}`.split('%').join(''));
  return isNaN(val) ? undefined : val;
};

export const DataChart = (props) => {
  const { data, hasData, loading, isDate, getDisplayVal, getHeaderTitle, showCounts, hasRange, colKey, findHeaderRow } = props;

  const classes = useStyles();

  const [state, setState] = useState({
    id: guid(),
    tables: makeArray(20),
    force: 0,
  });

  const getCanvasId = (i) => {
    return `chart_${state.id}_${i}`;
  };

  const hasChartIndex = props.chartIndex !== undefined;

  useEffect(() => {
    let charts = [];

    const chart = { ...props.chart };

    if (hasData && !loading && !chart.error) {
      chart.tables.map((t, i) => {
        if (hasChartIndex && i !== props.chartIndex) {
          return charts.push(null);
        }

        const ctx = document.getElementById(getCanvasId(i));

        const col = cleanCopy(chart.cols[i]);

        let range = [null, null];
        const calcRanges = (v) => {
          if (range[0] == null || range[1] === null) {
            range[0] = v;
            range[1] = v;
            return;
          }

          if (v < range[0]) {
            range[0] = v;
          }

          if (v > range[1]) {
            range[1] = v;
          }
        };

        let header = cleanCopy(chart.header);
        let sets = cleanCopy(['avg', ...t.rows]);

        if (showCounts) {
          header.shift();
        }

        const config = {
          type: isDate ? 'line' : 'bar',
          data: {
            labels: header.map((h) => {
              const ht = getHeaderTitle({ ...props, val: h });
              return get(ht, 'header.name', get(ht, 'header', ht.val));
            }),
            datasets: sets.map((tr, tri) => {
              if (tr === 'avg') {
                if (showCounts) {
                  col.shift();
                }
                return {
                  grouped: true,
                  label: 'Average',
                  data: col.map((c, _i) => {
                    const ck = colKey(t);
                    const foundHeaderRow = findHeaderRow(ck, c);
                    const headerRowItems = _i === 0 ? findHeaderRow(ck, get(chart, `cols.${i}`), true) || [] : [];
                    const value = ck
                      ? _i === 0
                        ? avgItems(headerRowItems.map((hr) => ({ value: parseFloat(get(hr, 'calculated_value', 0)) })))
                        : parseFloat(get(foundHeaderRow, 'calculated_value'))
                      : avgItems(c);

                    const val = Math.max(makeDisplayNumber(getDisplayVal({ val: { ...first(c), value } }).display), 0);
                    calcRanges(val);
                    return val;
                  }),
                  backgroundColor: COLORS[tri],
                  borderColor: COLORS[tri],
                  hidden: false,
                  spanGaps: true,
                };
              } else {
                const [label, ...rows] = tr;
                if (showCounts) {
                  rows.shift();
                }
                return {
                  grouped: true,
                  label: label.value,
                  data: rows.map((cc) => {
                    const val = makeDisplayNumber(getDisplayVal({ val: { ...cc } }).display);
                    calcRanges(val);
                    return val;
                  }),
                  backgroundColor: COLORS[tri],
                  borderColor: COLORS[tri],
                  hidden: isDate ? false : true,
                  spanGaps: true,
                };
              }
            }),
          },
          options: {
            plugins: {
              legend: {
                display: false,
              },
            },
          },
        };

        if (hasRange && !showCounts) {
          const configrange = [get(t, 'rows.0.1.config.min_score', 0), get(t, 'rows.0.1.config.max_score', 1)].map((p) => parseInt(p));
          const isPercent = configrange[1] == 1;
          const diff = configrange[1] - configrange[0];
          const divi = isPercent ? 1 : 100;
          const [min, max] = [(props.slide[0] / divi) * diff + configrange[0], (props.slide[1] / divi) * diff + configrange[0]];
          set(config, 'options.scales.y', {
            min,
            max,
          });
        } else {
          set(config, 'options.scales.y', {
            min: range[0],
            max: range[1],
          });
        }

        charts.push(new Chart(ctx, config));
      });
    }

    setState((s) => ({ ...s, charts }));

    return () => {
      charts.map((c) => c && c.destroy && c.destroy());
    };
  }, [props.chart, hasData, data, loading, showCounts]);

  const changeLabel = (e, ch) => {
    const vals = e.target.value;

    ch.legend.legendItems.map((li, i) => {
      const meta = ch.getDatasetMeta(i);
      meta.hidden = !vals.includes(meta.label);
    });
    ch.update();
    setState((s) => ({ ...s, force: s.force + 1 }));
  };

  const renderValue = (val) => {
    return val.join(', ');
  };

  const isSingleChart = !hasChartIndex;

  return (
    <div className={hasChartIndex ? '' : classes.chartWrap}>
      {state.tables.map((t, i) => {
        const exists = hasChartIndex ? i === props.chartIndex : get(props.chart, `tables.${i}`);
        const ch = get(state, `charts.${i}`);
        return (
          <div key={i} className={clsx(isSingleChart && classes.chartWrapEach, classes.container, hasChartIndex && classes.removeBox)} style={!!exists ? {} : { display: 'none' }}>
            {!!exists && !hasChartIndex && props.renderBar(exists, i, { includeTitle: true })}
            <div style={{ display: 'flex', flexWrap: 'wrap', maxWidth: '98%', paddingBottom: '1em' }}>
              <DropDown
                label="Series"
                renderValue={renderValue}
                value={get(ch, 'legend.legendItems', [])
                  .filter((t) => !t.hidden)
                  .map((t) => t.text)}
                multiple
                options={get(ch, 'legend.legendItems', []).map((t) => ({
                  id: t.text,
                  name: (
                    <>
                      <ListItemIcon>
                        <div style={{ width: 20, height: 20, backgroundColor: t.fillStyle }} />
                      </ListItemIcon>
                      <span>{t.text}</span>
                    </>
                  ),
                }))}
                onChange={(e) => changeLabel(e, ch)}
              />
            </div>
            <canvas id={getCanvasId(i)} />
          </div>
        );
      })}
    </div>
  );
};
